/*
 * Copyright (c) 2026 GS Elektromedizinische Geraete G. Stemple GmbH, Germany - all rights reserved.
 * <p>
 * DO NOT ALTER OR REMOVE COPYRIGHT NOTICES OR THIS FILE HEADER.
 */

import _ from 'lodash';
import { NormalizedPatient, normalizePatient } from '../../../models/patient';
import { Patient } from '../../../backendModels/patient.model';
import { normalizeMissionData, sanitizeMission } from '../../../models/operational';
import { MissionData } from '../../../backendModels/mission-data.model';
import { SynchronizedBaseData, SynchronizedMissionData } from '../../../models/diviDataUpdate';
import { PatientPropertyKeys } from '../../../models/genericElements/patientData';

/* participants are only part of OperationalData and not even there, because they are not synchronized */
export const controlCenterPropertiesOfMissionDataForPatientMaster = [
  'controlCenter',
  'controlCenterLicensePlate',
  'location',
  'personnelNumber',
];

const diviOrder: string[] = [
  'surname',
  'name',
  'birthday',
  'age',
  'newbornAge',
  'street',
  'postalCode',
  'city',
  'insuranceName',
  'insuranceNumber',
  'policyNumber',
  'gender',
  'patientId',
  'personnelNumber',
  'location',
  'controlCenter',
  'controlCenterLicensePlate',
];

export function compareByDiviOrder(a: [string, string | number | null], b: [string, string | number | null]) {
  return diviOrder.indexOf(a[0]) - diviOrder.indexOf(b[0]);
}

export function calculatePatientDiff(
  patient: Patient | undefined,
  lastSyncedBaseData: SynchronizedBaseData | undefined
) {
  const normalizedBackendPatient = normalizePatient(_.omit(patient, ['symptomOnset']));

  const diff = _.differenceWith(
    _.toPairs(normalizedBackendPatient),
    _.toPairs(normalizePatient(_.omit(lastSyncedBaseData?.patient, ['symptomOnset']))),
    _.isEqual
  );
  return markAllAgePropsChanged(diff, normalizedBackendPatient);
}

/*
  If one of birthday, age or newbornAge is changed, we mark them all as changed.
  This way we avoid trouble if e.g. TNA has set the birthday and paramedic has set the age.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function markAllAgePropsChanged(diff: [string, any][], normalizedBackendPatient: NormalizedPatient) {
  if (
    diff.some(
      (diffItem) =>
        diffItem[0] === PatientPropertyKeys.birthday ||
        diffItem[0] === PatientPropertyKeys.age ||
        diffItem[0] === 'newbornAge'
    )
  ) {
    if (!diff.some((diffItem) => diffItem[0] === PatientPropertyKeys.birthday)) {
      diff.push([PatientPropertyKeys.birthday, normalizedBackendPatient.birthday]);
    }

    if (!diff.some((diffItem) => diffItem[0] === PatientPropertyKeys.age)) {
      diff.push([PatientPropertyKeys.age, normalizedBackendPatient.age]);
    }

    if (!diff.some((diffItem) => diffItem[0] === 'newbornAge')) {
      diff.push(['newbornAge', normalizedBackendPatient.newbornAge]);
    }
  }
  return diff;
}

export function calculateMissionDataDiff(
  missionData: MissionData | undefined,
  lastSynchronizedMissionData: SynchronizedMissionData | undefined
) {
  return _.differenceWith(
    _.toPairs(
      normalizeMissionData(_.pick(missionData, ['controlCenter'], controlCenterPropertiesOfMissionDataForPatientMaster))
    ),
    _.toPairs(
      sanitizeMission(
        _.pick(lastSynchronizedMissionData?.mission, controlCenterPropertiesOfMissionDataForPatientMaster)
      )
    ),
    _.isEqual
  );
}
